import "./app.css";
import {
  TARGET_DIMS,
  type ConvertInput,
  type ConvertResult,
  type Orientation,
  detectOrientation,
  downloadPng,
} from "./convert";
import { Editor, type Tool } from "./editor";
import { icon, type IconName } from "./icons";
import ConvertWorker from "./convert.worker.ts?worker";

const ALLOWED_MIME = new Set(["image/jpeg", "image/png", "image/webp"]);
const MAX_FILE_BYTES = 25 * 1024 * 1024;

interface State {
  orientation: Orientation;
  result: ArrayBuffer | null;
  resultUrl: string | null;
  converting: boolean;
}

const state: State = {
  orientation: "portrait",
  result: null,
  resultUrl: null,
  converting: false,
};

/**
 * Generation counter for input state. Any change that invalidates a pending
 * conversion bumps this. runConvert captures the generation it started with
 * and discards its result if the generation has advanced by completion time.
 */
let inputGeneration = 0;

const $ = <T extends HTMLElement>(id: string): T => {
  const el = document.getElementById(id);
  if (!el) throw new Error(`#${id} not found`);
  return el as T;
};

const dropzone = $<HTMLDivElement>("dropzone");
const fileInput = $<HTMLInputElement>("fileInput");
const sectionEdit = $<HTMLElement>("sectionEdit");
const sectionResult = $<HTMLElement>("sectionResult");
const orPortrait = $<HTMLButtonElement>("orPortrait");
const orLandscape = $<HTMLButtonElement>("orLandscape");
const brushS = $<HTMLButtonElement>("brushS");
const brushM = $<HTMLButtonElement>("brushM");
const brushL = $<HTMLButtonElement>("brushL");
const toolPen = $<HTMLButtonElement>("toolPen");
const toolEraser = $<HTMLButtonElement>("toolEraser");
const toolBucket = $<HTMLButtonElement>("toolBucket");
const toolMagic = $<HTMLButtonElement>("toolMagic");
const lineBtn = $<HTMLButtonElement>("lineBtn");
const fillBtn = $<HTMLButtonElement>("fillBtn");
const clearBtn = $<HTMLButtonElement>("clearBtn");
const undoBtn = $<HTMLButtonElement>("undoBtn");
const redoBtn = $<HTMLButtonElement>("redoBtn");
const maskVisBtn = $<HTMLButtonElement>("maskVisBtn");
const stampVisBtn = $<HTMLButtonElement>("stampVisBtn");
const toolHand = $<HTMLButtonElement>("toolHand");
const stampScaleDown = $<HTMLButtonElement>("stampScaleDown");
const stampScaleUp = $<HTMLButtonElement>("stampScaleUp");
const zoomOutBtn = $<HTMLButtonElement>("zoomOutBtn");
const zoomInBtn = $<HTMLButtonElement>("zoomInBtn");
const zoomResetBtn = $<HTMLButtonElement>("zoomResetBtn");
const zoomLabel = $<HTMLSpanElement>("zoomLabel");
const editorCell = $<HTMLDivElement>("editorCell");
const editorCanvas = $<HTMLCanvasElement>("editorCanvas");
const toolStatus = $<HTMLSpanElement>("toolStatus");
const editorHint = $<HTMLSpanElement>("editorHint");
const resultCell = $<HTMLDivElement>("resultCell");
const convertBtn = $<HTMLButtonElement>("convertBtn");
const downloadBtn = $<HTMLButtonElement>("downloadBtn");
const statusEl = $<HTMLSpanElement>("status");
const shareLink = $<HTMLAnchorElement>("shareLink");

// Resolve icon placeholders <span class="icon" data-icon="...">
for (const el of document.querySelectorAll<HTMLElement>("[data-icon]")) {
  const name = el.dataset.icon as IconName | undefined;
  if (!name) continue;
  const size = el.classList.contains("icon-lg") ? 32 : 18;
  el.innerHTML = icon(name, size);
}

const editor = new Editor(editorCanvas, {
  onChange: () => invalidateResult(),
  onHistoryChange: () => updateHistoryButtons(),
});

function updateHistoryButtons(): void {
  undoBtn.disabled = !editor.canUndo;
  redoBtn.disabled = !editor.canRedo;
}

// ===== brush cursor overlay (visualizes brush diameter on mouse pointer) =====

const canvasWrapEl = editorCell.querySelector<HTMLElement>(".canvas-wrap");
if (!canvasWrapEl) throw new Error("editor canvas-wrap not found");
const editorWrap: HTMLElement = canvasWrapEl;
const brushCursor = document.createElement("div");
brushCursor.className = "brush-cursor";
editorWrap.appendChild(brushCursor);

function isBrushTool(): boolean {
  return editor.tool === "pen" || editor.tool === "eraser";
}

function updateBrushCursorMode(): void {
  const enabled = editor.hasSource() && isBrushTool();
  editorCanvas.classList.toggle("brush-mode", enabled);
  if (!enabled) brushCursor.classList.remove("visible");
}

function updateBrushCursorSize(): void {
  if (editorCanvas.width === 0) return;
  const rect = editorCanvas.getBoundingClientRect();
  if (rect.width === 0) return;
  const ratio = rect.width / editorCanvas.width;
  const sizeCss = Math.max(4, editor.penSize * ratio);
  brushCursor.style.width = `${sizeCss}px`;
  brushCursor.style.height = `${sizeCss}px`;
}

editorCanvas.addEventListener("pointerenter", (e) => {
  if (e.pointerType !== "mouse") return;
  if (!isBrushTool() || !editor.hasSource()) return;
  brushCursor.classList.add("visible");
  updateBrushCursorSize();
});

editorCanvas.addEventListener("pointerleave", () => {
  brushCursor.classList.remove("visible");
});

editorCanvas.addEventListener("pointermove", (e) => {
  if (e.pointerType !== "mouse") return;
  if (!brushCursor.classList.contains("visible")) {
    if (isBrushTool() && editor.hasSource()) {
      brushCursor.classList.add("visible");
      updateBrushCursorSize();
    } else {
      return;
    }
  }
  const wrapRect = editorWrap.getBoundingClientRect();
  // The cursor lives in the wrap's scrollable content space, so viewport
  // coordinates must be shifted by the current pan offset.
  brushCursor.style.left = `${e.clientX - wrapRect.left + editorWrap.scrollLeft}px`;
  brushCursor.style.top = `${e.clientY - wrapRect.top + editorWrap.scrollTop}px`;
});

new ResizeObserver(() => updateBrushCursorSize()).observe(editorCanvas);

// ===== preview zoom (view state — never touches editor history) =====

const ZOOM_LEVELS = [1, 1.5, 2, 3, 4] as const;
let zoomIndex = 0;

function applyZoom(): void {
  const zoom = ZOOM_LEVELS[zoomIndex];
  // Reset to the responsive (CSS-governed) layout, then measure the natural
  // width so `base × zoom` stays correct across orientation/viewport changes.
  editorCanvas.style.width = "";
  editorWrap.classList.remove("zoomed");
  if (zoom > 1 && editorCanvas.width > 0) {
    const base = editorCanvas.getBoundingClientRect().width;
    if (base > 0) {
      editorCanvas.style.width = `${base * zoom}px`;
      editorWrap.classList.add("zoomed");
    }
  }
  zoomLabel.textContent = `${Math.round(zoom * 100)}%`;
  zoomOutBtn.disabled = zoomIndex === 0;
  zoomInBtn.disabled = zoomIndex === ZOOM_LEVELS.length - 1;
  // Hand only has something to pan when zoomed in.
  toolHand.disabled = zoomIndex === 0;
  if (toolHand.disabled && editor.tool === "hand") {
    setTool("pen", toolPen);
  }
  updateBrushCursorSize();
}

function setZoomIndex(i: number): void {
  const next = Math.max(0, Math.min(ZOOM_LEVELS.length - 1, i));
  if (next === zoomIndex) return;
  zoomIndex = next;
  applyZoom();
}

function resetZoom(): void {
  if (zoomIndex === 0) return;
  zoomIndex = 0;
  applyZoom();
}

// ===== hand tool panning (view state) =====

let panPointerId: number | null = null;
let panOrigin = { x: 0, y: 0, left: 0, top: 0 };

editorCanvas.addEventListener("pointerdown", (e) => {
  if (editor.tool !== "hand" || !editor.hasSource()) return;
  e.preventDefault();
  panPointerId = e.pointerId;
  panOrigin = {
    x: e.clientX,
    y: e.clientY,
    left: editorWrap.scrollLeft,
    top: editorWrap.scrollTop,
  };
  editorCanvas.setPointerCapture(e.pointerId);
  editorWrap.classList.add("panning");
});

editorCanvas.addEventListener("pointermove", (e) => {
  if (panPointerId !== e.pointerId) return;
  editorWrap.scrollLeft = panOrigin.left - (e.clientX - panOrigin.x);
  editorWrap.scrollTop = panOrigin.top - (e.clientY - panOrigin.y);
});

const endPan = (e: PointerEvent): void => {
  if (panPointerId !== e.pointerId) return;
  panPointerId = null;
  editorWrap.classList.remove("panning");
  try {
    editorCanvas.releasePointerCapture(e.pointerId);
  } catch {
    // ignore
  }
};
editorCanvas.addEventListener("pointerup", endPan);
editorCanvas.addEventListener("pointercancel", endPan);

let convertWorker: Worker | null = null;
function getWorker(): Worker {
  if (!convertWorker) convertWorker = new ConvertWorker();
  return convertWorker;
}

function setOrientation(o: Orientation): void {
  state.orientation = o;
  orPortrait.setAttribute("aria-pressed", String(o === "portrait"));
  orLandscape.setAttribute("aria-pressed", String(o === "landscape"));
  editor.setOrientation(o);
  resetZoom();
  invalidateResult();
}

const TOOL_NAMES: Record<Tool, string> = {
  pen: "Pen",
  eraser: "Eraser",
  bucket: "Bucket",
  magic: "Magic",
  hand: "Hand",
};

let brushLabel = "M";

function updateToolStatus(): void {
  let text = TOOL_NAMES[editor.tool];
  if (editor.tool === "pen" || editor.tool === "eraser") {
    text += ` · ${brushLabel}`;
    if (editor.lineMode) text += " · Line";
  }
  toolStatus.textContent = text;
}

function setBrush(size: number, btn: HTMLButtonElement): void {
  editor.setPenSize(size);
  if (editor.tool !== "pen" && editor.tool !== "eraser") {
    setTool("pen", toolPen);
  }
  brushLabel = btn === brushS ? "S" : btn === brushL ? "L" : "M";
  for (const b of [brushS, brushM, brushL]) {
    b.setAttribute("aria-pressed", String(b === btn));
  }
  updateBrushCursorSize();
  updateToolStatus();
}

function setTool(tool: Tool, btn: HTMLButtonElement): void {
  editor.setTool(tool);
  for (const b of [toolPen, toolEraser, toolBucket, toolMagic, toolHand]) {
    b.setAttribute("aria-pressed", String(b === btn));
  }
  editorWrap.classList.toggle("pan-tool", tool === "hand");
  editorCanvas.classList.toggle("tool-bucket", tool === "bucket");
  editorCanvas.classList.toggle("tool-magic", tool === "magic");
  // Size and Line only apply to pen/eraser — dim them elsewhere (clicking
  // them still works and switches back to pen).
  const applicable = tool === "pen" || tool === "eraser";
  for (const el of document.querySelectorAll(".tool-option")) {
    el.classList.toggle("inapplicable", !applicable);
  }
  lineBtn.setAttribute("aria-pressed", String(editor.lineMode));
  updateBrushCursorMode();
  updateToolStatus();
}

function setMaskVisible(v: boolean): void {
  editor.setMaskVisible(v);
  maskVisBtn.setAttribute("aria-pressed", String(v));
  const iconEl = maskVisBtn.querySelector<HTMLElement>("[data-icon]");
  if (iconEl) iconEl.innerHTML = icon(v ? "eye" : "eyeOff", 18);
}

async function handleStampToggle(): Promise<void> {
  const next = !editor.stamp.visible;
  try {
    await editor.setStampVisible(next);
  } catch (err) {
    console.error(err);
    setStatus(`Stamp load failed · ${(err as Error).message}`, "error");
    stampVisBtn.setAttribute("aria-pressed", "false");
    return;
  }
  stampVisBtn.setAttribute("aria-pressed", String(next));
  stampScaleDown.disabled = !next;
  stampScaleUp.disabled = !next;
}

function invalidateResult(): void {
  inputGeneration++;
  if (state.resultUrl) URL.revokeObjectURL(state.resultUrl);
  state.result = null;
  state.resultUrl = null;
  downloadBtn.disabled = true;
  renderResultCell(null);
  setStatus("");
}

function setStatus(text: string, kind?: "in-progress" | "error" | "done"): void {
  statusEl.classList.remove("in-progress", "error");
  if (kind === "in-progress") statusEl.classList.add("in-progress");
  if (kind === "error") statusEl.classList.add("error");
  statusEl.replaceChildren();
  if (!text) return;
  const dot = document.createElement("span");
  dot.className = "dot";
  statusEl.appendChild(dot);
  statusEl.appendChild(document.createTextNode(text));
}

function renderResultCell(url: string | null): void {
  const wrap = resultCell.querySelector(".canvas-wrap");
  if (!wrap) return;
  wrap.replaceChildren();
  if (url) {
    resultCell.classList.remove("empty");
    const img = document.createElement("img");
    img.src = url;
    img.alt = "Result preview";
    wrap.appendChild(img);
  } else {
    resultCell.classList.add("empty");
    const span = document.createElement("span");
    span.className = "dropzone-hint";
    span.textContent = "Awaiting conversion";
    wrap.appendChild(span);
  }
}

function smoothScrollIntoView(el: HTMLElement): void {
  el.scrollIntoView({ behavior: "smooth", block: "start" });
}

function validateFile(file: File): string | null {
  if (file.size > MAX_FILE_BYTES) {
    return `File too large · ${(file.size / 1024 / 1024).toFixed(1)} MB > 25 MB`;
  }
  if (file.type && !ALLOWED_MIME.has(file.type)) {
    return `Unsupported type · ${file.type}`;
  }
  if (!file.type) {
    const ext = file.name.toLowerCase().match(/\.(jpe?g|png|webp)$/);
    if (!ext) return "Unsupported file";
  }
  return null;
}

async function adoptFile(file: File): Promise<void> {
  const reason = validateFile(file);
  if (reason) {
    setStatus(reason, "error");
    return;
  }
  setStatus("Loading…", "in-progress");

  let detected: Orientation | null = null;
  try {
    const bm = await createImageBitmap(file, { imageOrientation: "from-image" });
    detected = detectOrientation(bm.width, bm.height);
    bm.close();
  } catch {
    // Auto-detect peek failed — keep current orientation
  }

  if (detected && detected !== state.orientation) {
    setOrientation(detected);
  }

  try {
    await editor.loadSource(file);
  } catch (err) {
    console.error(err);
    setStatus(`Failed to load image · ${(err as Error).message}`, "error");
    fileInput.value = "";
    return;
  }

  editorHint.classList.add("hidden");
  editorCell.classList.remove("empty");
  resetZoom();
  invalidateResult();
  convertBtn.disabled = false;
  updateBrushCursorMode();

  sectionEdit.hidden = false;
  setStatus(`Loaded · ${file.name}`);
  fileInput.value = "";
  smoothScrollIntoView(sectionEdit);
}

async function runConvert(): Promise<void> {
  if (!editor.hasSource() || state.converting) return;
  const myGeneration = inputGeneration;
  state.converting = true;
  convertBtn.disabled = true;
  downloadBtn.disabled = true;
  setStatus("Converting…", "in-progress");

  try {
    const baked = editor.bake();
    const result = await postToWorker(baked);
    if (myGeneration !== inputGeneration) {
      // Inputs changed during conversion — discard stale result.
      console.info("Discarded stale conversion result");
      return;
    }
    state.result = result.pngBuffer;
    if (state.resultUrl) URL.revokeObjectURL(state.resultUrl);
    const blob = new Blob([result.pngBuffer], { type: "image/png" });
    state.resultUrl = URL.createObjectURL(blob);
    renderResultCell(state.resultUrl);
    downloadBtn.disabled = false;
    sectionResult.hidden = false;
    const { w, h } = TARGET_DIMS[state.orientation];
    setStatus(
      `Done · ${w}×${h} · ${(result.pngBuffer.byteLength / 1024).toFixed(0)} KB`,
      "done"
    );
    smoothScrollIntoView(sectionResult);
  } catch (err) {
    if (myGeneration !== inputGeneration) return;
    console.error(err);
    setStatus(`Failed · ${(err as Error).message}`, "error");
  } finally {
    state.converting = false;
    convertBtn.disabled = !editor.hasSource();
  }
}

function postToWorker(input: ConvertInput): Promise<ConvertResult> {
  return new Promise((resolve, reject) => {
    const worker = getWorker();
    const onMessage = (e: MessageEvent<{ ok: boolean; result?: ConvertResult; error?: string }>) => {
      worker.removeEventListener("message", onMessage);
      worker.removeEventListener("error", onError);
      if (e.data.ok && e.data.result) resolve(e.data.result);
      else reject(new Error(e.data.error ?? "Worker error"));
    };
    const onError = (e: ErrorEvent) => {
      worker.removeEventListener("message", onMessage);
      worker.removeEventListener("error", onError);
      reject(new Error(e.message || "Worker crashed"));
    };
    worker.addEventListener("message", onMessage);
    worker.addEventListener("error", onError);
    worker.postMessage(
      { input, options: { parity: "even" } },
      [input.rgba.buffer as ArrayBuffer, input.protectedAlpha.buffer as ArrayBuffer]
    );
  });
}

function runDownload(): void {
  if (!state.result) return;
  const ts = new Date().toISOString().replace(/[-:T.Z]/g, "").slice(0, 14);
  downloadPng(state.result, `tap_${ts}.png`);
}

function setupShareLink(): void {
  const url = "https://tap.ai.yami.net";
  const text = "サムネは、おあずけ。タップで、はじめて見える。";
  const hashtag = "TapImage";
  const params = new URLSearchParams({ text, url, hashtags: hashtag });
  shareLink.href = `https://twitter.com/intent/tweet?${params.toString()}`;

  shareLink.addEventListener("click", (e) => {
    if (!navigator.share) return; // default href opens X intent in new tab
    e.preventDefault();
    void (async () => {
      try {
        await navigator.share({
          title: "Tap!",
          text: `${text} #${hashtag}`,
          url,
        });
      } catch {
        window.open(shareLink.href, "_blank", "noopener,noreferrer");
      }
    })();
  });
}

// ===== wiring =====

setupShareLink();

dropzone.addEventListener("click", () => fileInput.click());
dropzone.addEventListener("keydown", (e) => {
  if (e.key === "Enter" || e.key === " ") {
    e.preventDefault();
    fileInput.click();
  }
});
dropzone.addEventListener("dragover", (e) => {
  e.preventDefault();
  dropzone.classList.add("dragover");
});
dropzone.addEventListener("dragleave", () => dropzone.classList.remove("dragover"));
dropzone.addEventListener("drop", (e) => {
  e.preventDefault();
  dropzone.classList.remove("dragover");
  const file = e.dataTransfer?.files?.[0];
  if (file) void adoptFile(file);
});

fileInput.addEventListener("change", () => {
  const file = fileInput.files?.[0];
  if (file) void adoptFile(file);
});

orPortrait.addEventListener("click", () => setOrientation("portrait"));
orLandscape.addEventListener("click", () => setOrientation("landscape"));

brushS.addEventListener("click", () => setBrush(12, brushS));
brushM.addEventListener("click", () => setBrush(48, brushM));
brushL.addEventListener("click", () => setBrush(96, brushL));
toolPen.addEventListener("click", () => setTool("pen", toolPen));
toolEraser.addEventListener("click", () => setTool("eraser", toolEraser));
toolBucket.addEventListener("click", () => setTool("bucket", toolBucket));
toolMagic.addEventListener("click", () => setTool("magic", toolMagic));
toolHand.addEventListener("click", () => setTool("hand", toolHand));
function toggleLineMode(): void {
  const next = !editor.lineMode;
  if (next && editor.tool !== "pen" && editor.tool !== "eraser") {
    setTool("pen", toolPen);
  }
  editor.setLineMode(next);
  lineBtn.setAttribute("aria-pressed", String(next));
  updateToolStatus();
}
lineBtn.addEventListener("click", toggleLineMode);
fillBtn.addEventListener("click", () => editor.fillMask());
clearBtn.addEventListener("click", () => editor.clearMask());
undoBtn.addEventListener("click", () => editor.undo());
redoBtn.addEventListener("click", () => editor.redo());
maskVisBtn.addEventListener("click", () => setMaskVisible(!editor.maskVisible));
stampVisBtn.addEventListener("click", () => void handleStampToggle());
stampScaleDown.addEventListener("click", () => editor.scaleStamp(0.8));
stampScaleUp.addEventListener("click", () => editor.scaleStamp(1.25));

zoomOutBtn.addEventListener("click", () => setZoomIndex(zoomIndex - 1));
zoomInBtn.addEventListener("click", () => setZoomIndex(zoomIndex + 1));
zoomResetBtn.addEventListener("click", () => resetZoom());

convertBtn.addEventListener("click", () => void runConvert());
downloadBtn.addEventListener("click", runDownload);

// ===== keyboard shortcuts (PC) =====

window.addEventListener("keydown", (e) => {
  if (e.isComposing) return;
  const target = e.target as HTMLElement | null;
  if (
    target &&
    (target.isContentEditable ||
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement ||
      target instanceof HTMLSelectElement)
  ) {
    return;
  }

  if (e.ctrlKey || e.metaKey) {
    const key = e.key.toLowerCase();
    if (key === "z" && !e.shiftKey) {
      e.preventDefault();
      if (editor.canUndo) editor.undo();
    } else if ((key === "z" && e.shiftKey) || key === "y") {
      e.preventDefault();
      if (editor.canRedo) editor.redo();
    }
    return;
  }

  if (e.altKey) return;

  switch (e.key.toLowerCase()) {
    case "p":
      setTool("pen", toolPen);
      break;
    case "e":
      setTool("eraser", toolEraser);
      break;
    case "g":
      setTool("bucket", toolBucket);
      break;
    case "w":
      setTool("magic", toolMagic);
      break;
    case "h":
      if (!toolHand.disabled) setTool("hand", toolHand);
      break;
    case "1":
      setBrush(12, brushS);
      break;
    case "2":
      setBrush(48, brushM);
      break;
    case "3":
      setBrush(96, brushL);
      break;
    case "l":
      toggleLineMode();
      break;
    case "m":
      setMaskVisible(!editor.maskVisible);
      break;
    case "t":
      void handleStampToggle();
      break;
    case "+":
    case "=":
      setZoomIndex(zoomIndex + 1);
      break;
    case "-":
      setZoomIndex(zoomIndex - 1);
      break;
    case "0":
      resetZoom();
      break;
    case "escape":
      editor.cancelLine();
      break;
  }
});

// Initialize defaults
applyZoom();
setBrush(48, brushM);
setTool("pen", toolPen);
setMaskVisible(true);
stampScaleDown.disabled = true;
stampScaleUp.disabled = true;
